import { useCallback, useEffect, useMemo, useState } from "react"
import moment from "moment"

import { MODO_IMPORTE, MODO_PIEZAS } from "./static"
import { suma, variacion } from "./format"

export const periodoDelMes = () =>
  `${moment().startOf("month").format("YYYY-MM-DD")}/${moment()
    .endOf("month")
    .format("YYYY-MM-DD")}`

export const etiquetaPeriodo = periodo => (periodo || "").replace("/", " → ")

/**
 * Estado de una tarjeta de serie temporal —Ventas o Compras—: periodo, modo de
 * medida, proveedor y las dos llamadas que alimentan la gráfica.
 *
 * No usa `hooks/useRequest` a propósito: allí cada `req()` vacía los datos
 * antes de pedir los nuevos, y la gráfica parpadearía en cada cambio de
 * periodo. Aquí los datos anteriores se mantienen hasta que llegan los
 * siguientes.
 */
export default function useTrendCard({ cargarImporte, cargarPiezasKgs }) {
  const [periodo, setPeriodo] = useState(periodoDelMes)
  const [modo, setModo] = useState(MODO_IMPORTE)
  const [proveedor, setProveedor] = useState("")

  const [importe, setImporte] = useState(null)
  const [kgs, setKgs] = useState(null)
  const [piezas, setPiezas] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const cargar = useCallback(
    async (rango, proveedorId) => {
      const config = { params: { period: rango, provider: proveedorId } }

      setIsLoading(true)
      try {
        const [serieImporte, seriePiezas] = await Promise.all([
          cargarImporte(config),
          cargarPiezasKgs(config),
        ])

        setImporte(serieImporte)
        setKgs(seriePiezas.kgs)
        setPiezas(seriePiezas.pieces)
      } catch (error) {
        // api_helper ya notifica el error
      } finally {
        setIsLoading(false)
      }
    },
    [cargarImporte, cargarPiezasKgs]
  )

  useEffect(() => {
    cargar(periodoDelMes(), "")
    // Solo al montar: los cambios posteriores los disparan los eventos.
  }, [])

  const onChangePeriodo = rango => {
    setPeriodo(rango)
    cargar(rango, proveedor)
  }

  const onChangeProveedor = proveedorId => {
    setProveedor(proveedorId)
    cargar(periodo, proveedorId)
  }

  /**
   * La gráfica del año anterior dibuja el periodo completo, pero el acumulado
   * de la tarjeta usa `previous_to_date`, que corta en el día equivalente de
   * hoy: comparar un mes en curso contra uno cerrado no dice nada.
   */
  const resumen = useMemo(() => {
    const principal = modo === MODO_IMPORTE ? importe : kgs
    if (!principal) return null

    const actual = suma(principal.current)
    const anterior = Number(principal.previous_to_date) || 0
    const serie = (principal.current || []).map(valor => Number(valor) || 0)
    const pico = serie.length ? Math.max(...serie) : 0

    return {
      actual,
      anterior,
      variacion: variacion(actual, anterior),
      promedio: serie.length ? actual / serie.length : 0,
      pico,
      diaPico: serie.length ? serie.indexOf(pico) + 1 : 0,
      // En «Piezas y Kgs» la tarjeta enseña las dos medidas a la vez.
      piezasActual: piezas ? suma(piezas.current) : 0,
      piezasAnterior: piezas ? Number(piezas.previous_to_date) || 0 : 0,
    }
  }, [modo, importe, kgs, piezas])

  return {
    state: {
      periodo,
      modo,
      proveedor,
      isLoading,
      resumen,
      labels: importe?.labels || [],
      series: { importe, kgs, piezas },
      // Total del periodo en importe, siempre: es lo que necesita el margen.
      totalImporte: importe ? suma(importe.current) : null,
    },
    events: {
      onChangePeriodo,
      onChangeProveedor,
      onChangeModo: setModo,
    },
  }
}

export const OPCIONES_MODO = [
  { id: MODO_IMPORTE, titulo: "Importe" },
  { id: MODO_PIEZAS, titulo: "Piezas y Kgs" },
]
